import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Rocket, CheckCircle2, AlertTriangle, Database, ShieldCheck } from "lucide-react";
import { pilotApi, apiErrorMessage } from "@/api/client";
import { toast } from "sonner";

const Pill = ({ live }) => (
  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${live ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
    {live ? "LIVE" : "HUMAN_REQUIRED"}
  </span>
);

export default function PilotDashboard() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [an, setAn] = useState(null);

  useEffect(() => {
    pilotApi.dashboard().then((r) => setD(r.data)).catch((e) => toast.error(apiErrorMessage(e)));
    pilotApi.analytics().then((r) => setAn(r.data)).catch(() => {});
  }, []);

  return (
    <div className="mx-auto max-w-3xl px-4 py-5 md:py-8" data-testid="pilot-dashboard">
      <button onClick={() => navigate("/more")} className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> More
      </button>
      <div className="flex items-center gap-2">
        <Rocket className="h-5 w-5 text-teal-600" />
        <h1 className="font-heading text-xl font-extrabold tracking-tight text-slate-900 md:text-2xl">Pilot Dashboard</h1>
        {d && <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${d.pilot_mode ? "bg-teal-600 text-white" : "bg-slate-200 text-slate-600"}`}>{d.pilot_mode ? "PILOT MODE ON" : "PILOT MODE OFF"}</span>}
      </div>
      {!d ? <div className="mt-4 h-40 animate-pulse rounded-xl border border-slate-200 bg-white" /> :
        <div className="mt-4 space-y-3">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="pilot-providers">
            <div className="mb-2 flex items-center gap-1.5 text-sm font-bold text-slate-800"><ShieldCheck className="h-4 w-4 text-teal-600" /> Providers</div>
            {Object.entries(d.providers).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between py-1 text-sm text-slate-600"><span>{k}</span><Pill live={v === "LIVE"} /></div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Monitored areas" value={d.scheduler?.monitored_areas} />
            <Stat label="Failing areas" value={d.scheduler?.failing_areas} tone={d.scheduler?.failing_areas ? "rose" : "slate"} />
            <Stat label="CH reviews pending" value={d.ch_reviews_pending} />
            <Stat label="Erasure requests" value={d.active_erasure_requests} />
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="pilot-tag-sites">
            <div className="mb-1 text-sm font-bold text-slate-800">Tag sites</div>
            {d.tag_sites?.length ? d.tag_sites.map((s, i) => (
              <div key={i} className="py-0.5 text-xs text-slate-500">{s.site_domain} <span className="text-slate-300">·</span> {s.product_code}</div>
            )) : <div className="text-xs text-slate-400">No product sites registered</div>}
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="pilot-retention">
            <div className="mb-1 flex items-center gap-1.5 text-sm font-bold text-slate-800"><Database className="h-4 w-4 text-teal-600" /> Retention (days)</div>
            <div className="grid grid-cols-2 gap-x-4 text-xs text-slate-500">
              {["website_events_days", "anonymous_sessions_days", "enrichment_facts_days", "notifications_days", "audit_events_days"].map((k) => (
                <div key={k} className="flex justify-between py-0.5"><span>{k.replace(/_days$/, "")}</span><b className="text-slate-700">{d.retention_policy?.[k]}</b></div>
              ))}
            </div>
          </div>
          {an && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="pilot-analytics">
              <div className="mb-1 text-sm font-bold text-slate-800">Activity (tenant aggregate)</div>
              <div className="grid grid-cols-2 gap-x-4 text-xs text-slate-500">
                {Object.entries(an).map(([k, v]) => (
                  <div key={k} className="flex justify-between py-0.5"><span>{k.replace(/_/g, " ")}</span><b className="text-slate-700">{v}</b></div>
                ))}
              </div>
            </div>
          )}
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="pilot-audit">
            <div className="mb-1 text-sm font-bold text-slate-800">Recent audit (20)</div>
            <ul className="space-y-0.5 text-[11px] text-slate-500">
              {(d.recent_audit || []).map((a) => (
                <li key={a.id} className="flex items-center gap-2"><span className="font-mono text-slate-400">{(a.created_at || "").slice(11, 19)}</span> {a.action}</li>
              ))}
            </ul>
          </div>
        </div>}
    </div>
  );
}

function Stat({ label, value, tone = "slate" }) {
  return (
    <div className={`rounded-xl border p-4 shadow-sm ${tone === "rose" ? "border-rose-200 bg-rose-50" : "border-slate-200 bg-white"}`}>
      <div className="text-2xl font-extrabold text-slate-900">{value ?? 0}</div>
      <div className="text-[11px] text-slate-400">{label}</div>
    </div>
  );
}
